import { Button, makeStyles, shorthands, Text, tokens, Tooltip } from '@fluentui/react-components';
import { ChatAdd24Regular } from '@fluentui/react-icons';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IGptConfiguration } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations, useGptConfigurations, useUserPreferences } from '../../store/hooks';
import { GptIcon } from '../Common/GptIcon';

interface MenuCommandsCollapsedProps {
    onNewChat: (gptConfiguration: IGptConfiguration) => void;
    showOnlyToggleMenuCommand?: boolean;
    onCloseMenuRequired?: () => void;
}

const useStyles = makeStyles({
    commands: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('10px')
    },
    
    gptItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.padding('8px'),        
        height: '48px'
    },
    gptName: {
        flex: 1
    }
});

const MenuCommandsCollapsed: FC<MenuCommandsCollapsedProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { onNewChat, showOnlyToggleMenuCommand, onCloseMenuRequired } = props;
    const { chats } = useConversations();
    const [isNewChatEnabled, setIsNewChatEnabled] = useState(false);
    const { gptConfigurations } = useGptConfigurations();
    const { userPreferences, dispatch } = useUserPreferences();
    const [addedGpts, setAddedGpts] = useState<IGptConfiguration[]>([]);

    useEffect(() => {
        // Look for the last conversation based on latest lastUpdateOn (or startedOn as fallback)
        // Create a copy before sorting to avoid mutating Redux state
        const lastConversation = [...chats].sort((a, b) => {
            const aDate = a.lastUpdateOn ?? a.startedOn;
            const bDate = b.lastUpdateOn ?? b.startedOn;
            return aDate > bDate ? -1 : 1;
        })[0];
        const isConversationEmpty = !lastConversation?.prompts || lastConversation.prompts.length === 0;
        setIsNewChatEnabled(!isConversationEmpty);
    }, [chats]);

    useEffect(() => {
        setAddedGpts(gptConfigurations.filter(x => userPreferences.addedGpts?.includes(x.id!) || x.isDefault).sort((a, b) => Number(b.isDefault) - Number(a.isDefault)))
    }, [userPreferences, gptConfigurations]);

    // If showing only toggle command (collapsed mode), don't render anything
    // since toggle is now handled by PageLayout
    if (showOnlyToggleMenuCommand) {
        return null;
    }

    return (
        <div className={classes.commands}>
            
                {addedGpts.map((gpt) => {
                    return <div key={gpt.id} className={classes.gptItem}>
                        <Tooltip content={gpt.isDefault ? t('menu.newChat') : (gpt.name || '')} relationship="label">
                            <div 
                                onClick={() => {
                                    onNewChat(gpt);
                                    // Close menu in mobile mode when creating new chat
                                    if (onCloseMenuRequired) {
                                        onCloseMenuRequired();
                                    }
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                {gpt.isDefault ? (
                                    <ChatAdd24Regular />                                        
                                ) : (
                                    <GptIcon gptId={gpt.id} theme={'light'} 
                                             width="25px"  />
                                )}
                            </div>
                        </Tooltip>
                    </div>
                })}            
            
        </div>
    );
};

export default MenuCommandsCollapsed;
